import React, { useCallback, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Settings as SettingsIcon, Lock, Bell, Globe } from 'lucide-react';
import api from '../api/axios';
import { toast } from 'sonner';

// Çeviri sistemi
const translations = {
  tr: {
    // Genel
    settings: 'Ayarlar',
    manageAccount: 'Hesabınızı ve tercihlerinizi yönetin',
    language: 'Dil',
    selectLanguage: 'Dil Seçin',
    
    // Hesap Bilgileri
    accountInformation: 'Hesap Bilgileri',
    accountDescription: 'Hesap detaylarınız ve kiracı bilgileri',
    companyName: 'Şirket Adı',
    email: 'E-posta',
    role: 'Rol',
    
    // Şifre Değiştir
    changePassword: 'Şifre Değiştir',
    changePasswordDescription: 'Hesabınızın şifresini güncelleyin',
    currentPassword: 'Mevcut Şifre',
    currentPasswordPlaceholder: 'Mevcut şifrenizi girin',
    newPassword: 'Yeni Şifre',
    newPasswordPlaceholder: 'Yeni şifrenizi girin (en az 6 karakter)',
    confirmPassword: 'Yeni Şifre (Tekrar)',
    confirmPasswordPlaceholder: 'Yeni şifrenizi tekrar girin',
    changePasswordButton: 'Şifreyi Değiştir',
    changing: 'Değiştiriliyor...',
    
    // Bildirimler
    notifications: 'Bildirimler',
    notificationsDescription: 'Bildirimleri nasıl alacağınızı yapılandırın',
    emailNotifications: 'E-posta Bildirimleri',
    emailNotificationsDescription: 'Yeni geri bildirimler için e-posta güncellemeleri alın',
    weeklySummary: 'Haftalık Özet',
    weeklySummaryDescription: 'Haftalık geri bildirim özetini alın',
    loading: 'Yükleniyor...',
    
    // Toast mesajları
    allFieldsRequired: 'Lütfen tüm alanları doldurun',
    pleaseFillThisField: 'Lütfen bu alanı doldurun',
    passwordMinLength: 'Yeni şifre en az 6 karakter olmalıdır',
    passwordsNotMatch: 'Yeni şifreler eşleşmiyor',
    passwordChanged: 'Şifre başarıyla değiştirildi',
    passwordChangeFailed: 'Şifre değiştirilemedi',
    preferencesUpdated: 'Bildirim tercihleri güncellendi',
    preferencesUpdateFailed: 'Bildirim tercihleri güncellenemedi',
    preferencesLoadFailed: 'Bildirim tercihleri yüklenemedi'
  },
  en: {
    settings: 'Settings',
    manageAccount: 'Manage your account and preferences',
    language: 'Language',
    selectLanguage: 'Select Language',
    accountInformation: 'Account Information',
    accountDescription: 'Your account details and tenant information',
    companyName: 'Company Name',
    email: 'Email',
    role: 'Role',
    changePassword: 'Change Password',
    changePasswordDescription: 'Update your account password',
    currentPassword: 'Current Password',
    currentPasswordPlaceholder: 'Enter your current password',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'Enter your new password (min 6 characters)',
    confirmPassword: 'New Password (Confirm)',
    confirmPasswordPlaceholder: 'Re-enter your new password',
    changePasswordButton: 'Change Password',
    changing: 'Changing...',
    notifications: 'Notifications',
    notificationsDescription: 'Configure how you receive notifications',
    emailNotifications: 'Email Notifications',
    emailNotificationsDescription: 'Receive email updates for new feedback',
    weeklySummary: 'Weekly Summary',
    weeklySummaryDescription: 'Receive weekly feedback summary',
    loading: 'Loading...',
    allFieldsRequired: 'Please fill in all fields',
    pleaseFillThisField: 'Please fill in this field',
    passwordMinLength: 'New password must be at least 6 characters',
    passwordsNotMatch: 'New passwords do not match',
    passwordChanged: 'Password changed successfully',
    passwordChangeFailed: 'Failed to change password',
    preferencesUpdated: 'Notification preferences updated',
    preferencesUpdateFailed: 'Failed to update notification preferences',
    preferencesLoadFailed: 'Failed to load notification preferences'
  },
  de: {
    settings: 'Einstellungen',
    manageAccount: 'Verwalten Sie Ihr Konto und Ihre Einstellungen',
    language: 'Sprache',
    selectLanguage: 'Sprache auswählen',
    accountInformation: 'Kontoinformationen',
    accountDescription: 'Ihre Kontodaten und Mandanteninformationen',
    companyName: 'Firmenname',
    email: 'E-Mail',
    role: 'Rolle',
    changePassword: 'Passwort ändern',
    changePasswordDescription: 'Aktualisieren Sie Ihr Kontopasswort',
    currentPassword: 'Aktuelles Passwort',
    currentPasswordPlaceholder: 'Geben Sie Ihr aktuelles Passwort ein',
    newPassword: 'Neues Passwort',
    newPasswordPlaceholder: 'Geben Sie Ihr neues Passwort ein (mind. 6 Zeichen)',
    confirmPassword: 'Neues Passwort (Bestätigen)',
    confirmPasswordPlaceholder: 'Geben Sie Ihr neues Passwort erneut ein',
    changePasswordButton: 'Passwort ändern',
    changing: 'Wird geändert...',
    notifications: 'Benachrichtigungen',
    notificationsDescription: 'Konfigurieren Sie, wie Sie Benachrichtigungen erhalten',
    emailNotifications: 'E-Mail-Benachrichtigungen',
    emailNotificationsDescription: 'E-Mail-Updates für neues Feedback erhalten',
    weeklySummary: 'Wöchentliche Zusammenfassung',
    weeklySummaryDescription: 'Wöchentliche Feedback-Zusammenfassung erhalten',
    loading: 'Wird geladen...',
    allFieldsRequired: 'Bitte füllen Sie alle Felder aus',
    pleaseFillThisField: 'Bitte füllen Sie dieses Feld aus',
    passwordMinLength: 'Neues Passwort muss mindestens 6 Zeichen lang sein',
    passwordsNotMatch: 'Neue Passwörter stimmen nicht überein',
    passwordChanged: 'Passwort erfolgreich geändert',
    passwordChangeFailed: 'Passwort konnte nicht geändert werden',
    preferencesUpdated: 'Benachrichtigungseinstellungen aktualisiert',
    preferencesUpdateFailed: 'Benachrichtigungseinstellungen konnten nicht aktualisiert werden',
    preferencesLoadFailed: 'Benachrichtigungseinstellungen konnten nicht geladen werden'
  },
  es: {
    settings: 'Configuración',
    manageAccount: 'Administre su cuenta y preferencias',
    language: 'Idioma',
    selectLanguage: 'Seleccionar Idioma',
    accountInformation: 'Información de la Cuenta',
    accountDescription: 'Los detalles de su cuenta e información del inquilino',
    companyName: 'Nombre de la Empresa',
    email: 'Correo Electrónico',
    role: 'Rol',
    changePassword: 'Cambiar Contraseña',
    changePasswordDescription: 'Actualice la contraseña de su cuenta',
    currentPassword: 'Contraseña Actual',
    currentPasswordPlaceholder: 'Ingrese su contraseña actual',
    newPassword: 'Nueva Contraseña',
    newPasswordPlaceholder: 'Ingrese su nueva contraseña (mín. 6 caracteres)',
    confirmPassword: 'Nueva Contraseña (Confirmar)',
    confirmPasswordPlaceholder: 'Vuelva a ingresar su nueva contraseña',
    changePasswordButton: 'Cambiar Contraseña',
    changing: 'Cambiando...',
    notifications: 'Notificaciones',
    notificationsDescription: 'Configure cómo recibe las notificaciones',
    emailNotifications: 'Notificaciones por Correo',
    emailNotificationsDescription: 'Reciba actualizaciones por correo para nuevos comentarios',
    weeklySummary: 'Resumen Semanal',
    weeklySummaryDescription: 'Reciba el resumen semanal de comentarios',
    loading: 'Cargando...',
    allFieldsRequired: 'Por favor complete todos los campos',
    pleaseFillThisField: 'Por favor complete este campo',
    passwordMinLength: 'La nueva contraseña debe tener al menos 6 caracteres',
    passwordsNotMatch: 'Las nuevas contraseñas no coinciden',
    passwordChanged: 'Contraseña cambiada exitosamente',
    passwordChangeFailed: 'No se pudo cambiar la contraseña',
    preferencesUpdated: 'Preferencias de notificación actualizadas',
    preferencesUpdateFailed: 'No se pudieron actualizar las preferencias de notificación',
    preferencesLoadFailed: 'No se pudieron cargar las preferencias de notificación'
  },
  fr: {
    settings: 'Paramètres',
    manageAccount: 'Gérez votre compte et vos préférences',
    language: 'Langue',
    selectLanguage: 'Sélectionner la Langue',
    accountInformation: 'Informations du Compte',
    accountDescription: 'Les détails de votre compte et les informations du locataire',
    companyName: 'Nom de l\'Entreprise',
    email: 'E-mail',
    role: 'Rôle',
    changePassword: 'Changer le Mot de Passe',
    changePasswordDescription: 'Mettez à jour le mot de passe de votre compte',
    currentPassword: 'Mot de Passe Actuel',
    currentPasswordPlaceholder: 'Entrez votre mot de passe actuel',
    newPassword: 'Nouveau Mot de Passe',
    newPasswordPlaceholder: 'Entrez votre nouveau mot de passe (min. 6 caractères)',
    confirmPassword: 'Nouveau Mot de Passe (Confirmer)',
    confirmPasswordPlaceholder: 'Ré-entrez votre nouveau mot de passe',
    changePasswordButton: 'Changer le Mot de Passe',
    changing: 'Changement en cours...',
    notifications: 'Notifications',
    notificationsDescription: 'Configurez comment vous recevez les notifications',
    emailNotifications: 'Notifications par E-mail',
    emailNotificationsDescription: 'Recevez des mises à jour par e-mail pour les nouveaux commentaires',
    weeklySummary: 'Résumé Hebdomadaire',
    weeklySummaryDescription: 'Recevez le résumé hebdomadaire des commentaires',
    loading: 'Chargement...',
    allFieldsRequired: 'Veuillez remplir tous les champs',
    pleaseFillThisField: 'Veuillez remplir ce champ',
    passwordMinLength: 'Le nouveau mot de passe doit contenir au moins 6 caractères',
    passwordsNotMatch: 'Les nouveaux mots de passe ne correspondent pas',
    passwordChanged: 'Mot de passe modifié avec succès',
    passwordChangeFailed: 'Échec de la modification du mot de passe',
    preferencesUpdated: 'Préférences de notification mises à jour',
    preferencesUpdateFailed: 'Échec de la mise à jour des préférences de notification',
    preferencesLoadFailed: 'Échec du chargement des préférences de notification'
  }
};

const languageNames = {
  tr: 'Türkçe',
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français'
};

export const Settings = () => {
  const { user, planInfo } = useAuth();
  const { language, setLanguage: updateGlobalLanguage, t: globalT } = useLanguage();
  const [localLanguage, setLocalLanguage] = useState(language);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    email_notifications: true,
    weekly_summary: true,
    language: 'tr'
  });
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [hasLoadedPreferences, setHasLoadedPreferences] = useState(false);

  const t = translations[localLanguage] || translations.tr;
  const isFreePlan = planInfo?.plan_type === 'free';

  const fetchNotificationPreferences = useCallback(async () => {
    try {
      setLoadingPreferences(true);
      const response = await api.get('/auth/notification-preferences');
      const prefs = response.data;
      setNotificationPreferences(prefs);
      
      // Only update language from backend on initial load
      // This prevents overwriting user's current selection after they change it
      if (!hasLoadedPreferences) {
        const backendLanguage = prefs.language || 'tr';
        // Only update if it's different from current global language
        if (backendLanguage !== language) {
          setLocalLanguage(backendLanguage);
          await updateGlobalLanguage(backendLanguage);
        } else if (backendLanguage !== localLanguage) {
          // Sync localLanguage with backend if they differ
          setLocalLanguage(backendLanguage);
        }
        setHasLoadedPreferences(true);
      }
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
      toast.error(t.preferencesLoadFailed);
    } finally {
      setLoadingPreferences(false);
    }
  }, [language, localLanguage, hasLoadedPreferences, t.preferencesLoadFailed, updateGlobalLanguage]);

  useEffect(() => {
    // Notifications are not supported on Free plan, so don't fetch / update notification preferences.
    if (!planInfo?.plan_type) return; // wait until plan is known
    if (isFreePlan) {
      setLoadingPreferences(false);
      return;
    }
    fetchNotificationPreferences();
  }, [planInfo?.plan_type, isFreePlan, fetchNotificationPreferences]);

  useEffect(() => {
    // Sync with global language context - but only if it's different and we're not currently saving
    if (language !== localLanguage && !savingPreferences) {
      setLocalLanguage(language);
    }
  }, [language, savingPreferences]);

  const handleLanguageChange = async (newLanguage) => {
    // Don't do anything if it's the same language
    if (newLanguage === localLanguage) {
      return;
    }

    // Store previous values for potential rollback
    const previousLanguage = localLanguage;
    const previousPreferences = notificationPreferences;

    // Update local state immediately for UI responsiveness
    setLocalLanguage(newLanguage);
    
    // Update global language context (this updates localStorage and backend)
    // LanguageContext handles backend update, so we don't need to do it here
    try {
      await updateGlobalLanguage(newLanguage);

      // On Free plan, we don't persist notification preferences (feature not supported).
      if (!isFreePlan) {
        // Update notification preferences state to reflect the change
        const updatedPreferences = {
          ...notificationPreferences,
          language: newLanguage
        };
        setNotificationPreferences(updatedPreferences);
        
        // Also update in backend (for notification preferences, not just language)
        setSavingPreferences(true);
        try {
          await api.put('/auth/notification-preferences', updatedPreferences);
        } catch (error) {
          // Revert notification preferences on error
          setNotificationPreferences(previousPreferences);
          console.error('Failed to update notification preferences:', error);
        } finally {
          setSavingPreferences(false);
        }
      }
    } catch (error) {
      // Revert on error - restore previous state
      setLocalLanguage(previousLanguage);
      // Global language context update failed, but we can't easily revert it
      // The error is already logged in LanguageContext
      console.error('Failed to update language:', error);
    }
  };

  const handleNotificationToggle = async (key, value) => {
    const updatedPreferences = {
      ...notificationPreferences,
      [key]: value
    };
    setNotificationPreferences(updatedPreferences);
    
    setSavingPreferences(true);
    try {
      await api.put('/auth/notification-preferences', updatedPreferences);
      toast.success(t.preferencesUpdated);
    } catch (error) {
      // Revert on error
      setNotificationPreferences(notificationPreferences);
      toast.error(t.preferencesUpdateFailed);
    } finally {
      setSavingPreferences(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error(t.allFieldsRequired);
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error(t.passwordMinLength);
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t.passwordsNotMatch);
      return;
    }

    setLoading(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success(t.passwordChanged);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t.passwordChangeFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Account Settings - FeedPanels</title>
        <meta name="description" content="Update your FeedPanels account: password, email, and notification preferences. Manage your profile and security." />
        <link rel="canonical" href="https://feedpanels.com/dashboard/settings" />
      </Helmet>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white" data-testid="settings-page-title">
                {t.settings}
              </h1>
              <div className="flex items-center space-x-2">
                <Globe className="w-4 md:w-5 h-4 md:h-5 text-gray-500 dark:text-gray-400" />
                <Select value={localLanguage} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-[150px] md:w-[180px]">
                    <SelectValue placeholder={t.selectLanguage} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tr">{languageNames.tr}</SelectItem>
                    <SelectItem value="en">{languageNames.en}</SelectItem>
                    <SelectItem value="de">{languageNames.de}</SelectItem>
                    <SelectItem value="es">{languageNames.es}</SelectItem>
                    <SelectItem value="fr">{languageNames.fr}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{t.manageAccount}</p>
          </div>

          <div className="space-y-4 md:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t.accountInformation}</CardTitle>
                <CardDescription>{t.accountDescription}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4">
                <div>
                  <Label className="text-sm md:text-base">{t.companyName}</Label>
                  <Input value={user?.name} disabled data-testid="settings-company-name" className="text-sm md:text-base" />
                </div>
                <div>
                  <Label className="text-sm md:text-base">{t.email}</Label>
                  <Input value={user?.email} disabled data-testid="settings-email" className="text-sm md:text-base" />
                </div>
                <div>
                  <Label className="text-sm md:text-base">{t.role}</Label>
                  <Input value={user?.role} disabled data-testid="settings-role" className="text-sm md:text-base" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Lock className="w-5 h-5" />
                  <span>{t.changePassword}</span>
                </CardTitle>
                <CardDescription>{t.changePasswordDescription}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-3 md:space-y-4">
                  <div>
                    <Label htmlFor="current_password" className="text-sm md:text-base">{t.currentPassword}</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => {
                        setPasswordData({ ...passwordData, current_password: e.target.value });
                        e.target.setCustomValidity('');
                      }}
                      onInvalid={(e) => {
                        e.target.setCustomValidity(t.pleaseFillThisField);
                      }}
                      placeholder={t.currentPasswordPlaceholder}
                      required
                      data-testid="current-password-input"
                      className="text-sm md:text-base"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_password" className="text-sm md:text-base">{t.newPassword}</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => {
                        setPasswordData({ ...passwordData, new_password: e.target.value });
                        e.target.setCustomValidity('');
                      }}
                      onInvalid={(e) => {
                        e.target.setCustomValidity(t.pleaseFillThisField);
                      }}
                      placeholder={t.newPasswordPlaceholder}
                      required
                      minLength={6}
                      data-testid="new-password-input"
                      className="text-sm md:text-base"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password" className="text-sm md:text-base">{t.confirmPassword}</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => {
                        setPasswordData({ ...passwordData, confirm_password: e.target.value });
                        e.target.setCustomValidity('');
                      }}
                      onInvalid={(e) => {
                        e.target.setCustomValidity(t.pleaseFillThisField);
                      }}
                      placeholder={t.confirmPasswordPlaceholder}
                      required
                      data-testid="confirm-password-input"
                      className="text-sm md:text-base"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full text-sm md:text-base" data-testid="change-password-button">
                    {loading ? t.changing : t.changePasswordButton}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {!isFreePlan && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Bell className="w-5 h-5" />
                    <span>{t.notifications}</span>
                  </CardTitle>
                  <CardDescription>{t.notificationsDescription}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 md:space-y-6">
                  {loadingPreferences ? (
                    <div className="text-center py-4 text-gray-500 text-sm md:text-base">{t.loading}</div>
                  ) : (
                    <>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 py-2">
                        <div className="flex-1">
                          <p className="font-medium text-sm md:text-base text-gray-900 dark:text-white">{t.emailNotifications}</p>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            {t.emailNotificationsDescription}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <Switch
                            checked={notificationPreferences.email_notifications}
                            onCheckedChange={(checked) => handleNotificationToggle('email_notifications', checked)}
                            disabled={savingPreferences}
                            data-testid="toggle-email-notifications"
                          />
                        </div>
                      </div>
                      <div className="border-t pt-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 py-2">
                          <div className="flex-1">
                            <p className="font-medium text-sm md:text-base text-gray-900 dark:text-white">{t.weeklySummary}</p>
                            <p className="text-xs md:text-sm text-gray-500 mt-1">
                              {t.weeklySummaryDescription}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <Switch
                              checked={notificationPreferences.weekly_summary}
                              onCheckedChange={(checked) => handleNotificationToggle('weekly_summary', checked)}
                              disabled={savingPreferences}
                              data-testid="toggle-weekly-summary"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
